import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { createConsultation } from "@/lib/api";
import { Loader2, CalendarCheck, Check } from "lucide-react";

export default function ConsultationDialog({ open, onOpenChange, context = {} }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", website: "", phone: "", country: "",
    timezone: "", service_interest: "", challenges: "", preferred_datetime: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "bg-white border-slate-200 rounded-lg h-11 focus-visible:ring-brand";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createConsultation({ ...form, ...context });
      setDone(true);
      toast.success("Strategy session requested — we'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => { onOpenChange(v); if (!v) setTimeout(() => setDone(false), 300); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white rounded-2xl max-w-lg" data-testid="consultation-dialog">
        {done ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-4">
              <Check size={26} />
            </div>
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Request received</h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              Our team will reach out to schedule your WebsiteTech strategy session. Check your inbox shortly.
            </p>
            <button onClick={() => close(false)} className="btn-outline rounded-lg px-6 py-2.5 mt-6">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-slate-900 flex items-center gap-2">
                <CalendarCheck size={20} className="text-brand" /> Book a Website Strategy Session
              </DialogTitle>
              <DialogDescription className="sr-only">Book a free 30-minute website strategy consultation with a WebsiteTech strategist.</DialogDescription>
            </DialogHeader>
            <p className="text-slate-500 text-sm -mt-1">A free 30-minute consultation with a WebsiteTech strategist.</p>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="consult-name" placeholder="Full name*" value={form.name} onChange={set("name")} className={inputCls} />
                <Input data-testid="consult-email" type="email" placeholder="Email*" value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} />
                <Input placeholder="Website" value={form.website} onChange={set("website")} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Country" value={form.country} onChange={set("country")} className={inputCls} />
                <Input placeholder="Time zone" value={form.timezone} onChange={set("timezone")} className={inputCls} />
              </div>
              <Input data-testid="consult-service" placeholder="Service interest (e.g. AI Website, SEO)" value={form.service_interest} onChange={set("service_interest")} className={inputCls} />
              <Input placeholder="Preferred date / time" value={form.preferred_datetime} onChange={set("preferred_datetime")} className={inputCls} />
              <Textarea placeholder="Current challenges" value={form.challenges} onChange={set("challenges")} className="bg-white border-slate-200 rounded-lg min-h-[70px] focus-visible:ring-brand" />
              <button type="submit" disabled={loading} data-testid="consult-submit" className="btn-primary rounded-lg w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Request Strategy Session"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
